import type { ColumnDef } from '@tanstack/react-table';
import {
  createActionsColumn,
  createSerialColumn,
  StackedCellText,
  WrappingCellText,
} from '@/components/common/DataTable';
import { EmptyValue } from '@/components/common/EmptyValue';
import { ExpandableComment } from '@/components/common/ExpandableComment';
import { RowActionButton } from '@/components/common/RowActions';
import { formatDisplayDate } from '@/utils/date';
import { DerCostCell, DerDecisionCell, type DerQueueColumnDeps } from './DerQueueCells';
import { getAreaColumn, getDistributorNameColumn } from './DistributorColumns';
import { VendorCell } from './VendorCell';
import { QUEUE_DECISION_COLUMN_ID } from '../constants/billing.constants';
import type { OfflineDerRow } from '../types/billing.types';
export function getDerQueueColumns(deps: DerQueueColumnDeps): ColumnDef<OfflineDerRow, unknown>[] {
  return [
    createSerialColumn<OfflineDerRow>(),
    getDistributorNameColumn<OfflineDerRow>(deps.searchText),
    getAreaColumn<OfflineDerRow>(),
    {
      accessorKey: 'vendor',
      header: 'Vendor / Person',
      cell: ({ row }) => (
        <VendorCell
          vendor={row.original.vendor}
          lat={row.original.vendorLat}
          lon={row.original.vendorLon}
          travelRoute={[row.original.fromLocation, row.original.toLocation]
            .filter(Boolean)
            .join(' → ')}
        />
      ),
    },
    {
      accessorKey: 'expenseType',
      header: 'Expense Type',
      cell: ({ row }) => <span className="text-sm">{row.original.expenseType ?? '—'}</span>,
    },
    {
      id: 'cost',
      header: 'Cost (Tk)',
      cell: ({ row }) => <DerCostCell row={row.original} deps={deps} />,
    },
    {
      accessorKey: 'comments',
      header: 'Comment',
      cell: ({ row }) => (
        <WrappingCellText>
          {row.original.comments ? (
            <ExpandableComment comment={row.original.comments} />
          ) : (
            <EmptyValue />
          )}
        </WrappingCellText>
      ),
    },
    {
      id: 'expenseDate',
      header: 'Expense Date',
      cell: ({ row }) => (
        <StackedCellText
          primary={formatDisplayDate(row.original.expenseDate)}
          secondary={`${row.original.entryAgeDays}d old`}
        />
      ),
    },
    {
      id: QUEUE_DECISION_COLUMN_ID,
      header: 'Status',
      cell: ({ row }) => <DerDecisionCell row={row.original} deps={deps} />,
    },
    createActionsColumn<OfflineDerRow>((der) =>
      der.hasAttachment ? (
        <RowActionButton
          label="Bill"
          ariaLabel={`Open the bill of ${der.name}`}
          isDisabled={deps.downloadingDetailId === der.detailId}
          onClick={() => deps.onDownload(der)}
        />
      ) : null,
    ),
  ];
}
